import { CheckExistOtpResponse } from '@porterin/booking-flow/src/auth/domain/api-models/CheckExistOtpApiModel';
import { CustomerExistsResponse } from '@porterin/booking-flow/src/auth/domain/api-models/CustomerExistsApiModel';
import { OTPValidationResponse } from '@porterin/booking-flow/src/auth/domain/api-models/OtpValidationApiModel';
import { SignupResponse } from '@porterin/booking-flow/src/auth/domain/api-models/SignupApiModel';
import { API_ENDPOINTS } from '@porterin/booking-flow/src/auth/domain/config/api';
import { Customer } from '@porterin/booking-flow/src/auth/domain/entities/Customer';
import { CustomerExists } from '@porterin/booking-flow/src/auth/domain/entities/CustomerExists';
import { CustomerSignup } from '@porterin/booking-flow/src/auth/domain/entities/CustomerSignup';
import { CustomerStatus } from '@porterin/booking-flow/src/auth/domain/entities/CustomerStatus';
import { toCustomerStatusEntity } from '@porterin/booking-flow/src/auth/domain/entity-mappers/CheckExistOtpEntityMapper';
import { toCustomerExistsEntity } from '@porterin/booking-flow/src/auth/domain/entity-mappers/CustomerExistsEntityMapper';
import { toCustomerEntity } from '@porterin/booking-flow/src/auth/domain/entity-mappers/OtpValidationEntityMapper';
import { toSignupEntity } from '@porterin/booking-flow/src/auth/domain/entity-mappers/SignupEntityMapper';
import {
  createCheckExistsHeaders,
  createCustomerExistsPayload,
  createHeaders,
  createNetworkError,
  createValidateOtpPayload,
} from '@porterin/booking-flow/src/auth/domain/helpers/authHelpers';
import { AgreedDocs } from '@porterin/booking-flow/src/order-review/domain/entities/AgreedDocs';
import { fireApiErrorSentry } from '@porterin/component-library/src/ts-utils/analytics/sentry/SentryHelpers';
import axios, { AxiosError } from 'axios';
const config = { agreed_docs_login_flag: 'true' };

export const checkExistOtp = async (
  webTrackingId: string,
  mobile: string,
): Promise<CustomerStatus> => {
  try {
    const headers = createHeaders(webTrackingId);
    const payload = createCustomerExistsPayload(mobile);

    const response = await axios.post<CheckExistOtpResponse>(
      `${API_ENDPOINTS.CUSTOMER_EXIST_OTP}`,
      payload,
      { headers },
    );

    return toCustomerStatusEntity(response.data);
  } catch (error) {
    fireApiErrorSentry(error, 'checkExistOtp');
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      throw createNetworkError(
        axiosError.response?.status ?? 500,
        (axiosError.response?.data as { error: string })?.error ??
          axiosError.message,
      );
    }
    throw error;
  }
};

export const checkCustomerExists = async (
  mobile: string,
): Promise<CustomerExists> => {
  try {
    const headers = createCheckExistsHeaders();

    const response = await axios.get<CustomerExistsResponse>(
      `${API_ENDPOINTS.CUSTOMER_CHECK_EXISTS}/${mobile}`,
      { headers },
    );

    return toCustomerExistsEntity(response.data);
  } catch (error) {
    fireApiErrorSentry(error, 'checkCustomerExists');
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      throw createNetworkError(
        axiosError.response?.status ?? 500,
        (axiosError.response?.data as { error: string })?.error ??
          axiosError.message,
      );
    }
    throw error;
  }
};

export const customerSignUp = async (
  webTrackingId: string,
  mobile: string,
  email: string,
  name: string,
): Promise<CustomerSignup> => {
  try {
    const headers = createHeaders(webTrackingId);
    const payload = { mobile, email, name };

    const response = await axios.post<SignupResponse>(
      `${API_ENDPOINTS.CUSTOMER_SIGNUP}`,
      payload,
      { headers },
    );

    return toSignupEntity(response.data);
  } catch (error) {
    fireApiErrorSentry(error, 'customerSignUp');
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      throw createNetworkError(
        axiosError.response?.status ?? 500,
        (axiosError.response?.data as { error: string })?.error ??
          axiosError.message,
      );
    }
    throw error;
  }
};

export const validateOtp = async (
  webTrackingId: string,
  userInfo: {
    otp: string;
    mobile: string;
    email?: string;
    name?: string;
    whatsappConsent?: boolean;
    customerFrequency?: string;
  },
  agreed_docs?: AgreedDocs,
  referral_code?: string,
  geo_region_id?: string,
): Promise<Customer> => {
  try {
    const { name, mobile, otp, email, whatsappConsent, customerFrequency } =
      userInfo;
    const headers = createHeaders(webTrackingId);
    const agreedDocsLoginFlag = Boolean(
      config['agreed_docs_login_flag'] as string | undefined,
    );
    const payload = createValidateOtpPayload(
      mobile,
      otp,
      agreedDocsLoginFlag,
      email,
      name,
      whatsappConsent,
      customerFrequency,
      agreed_docs,
      referral_code,
      geo_region_id,
    );

    const response = await axios.post<OTPValidationResponse>(
      `${API_ENDPOINTS.CUSTOMER_VALIDATE_OTP}`,
      payload,
      { headers },
    );

    return toCustomerEntity(response.data);
  } catch (error) {
    fireApiErrorSentry(error, 'validateOtp');
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      throw createNetworkError(
        axiosError.response?.status ?? 500,
        (axiosError.response?.data as { error: string })?.error ??
          axiosError.message,
      );
    }
    throw error;
  }
};
